import { type FormEvent, useEffect, useState } from 'react';
import { ApiError, OfflineError } from '../api/client';
import { type Role, useSession } from '../api/session';
import {
  type User,
  createUser,
  deleteUser,
  generatePassword,
  isSelfTarget,
  listUsers,
  updateUser,
} from '../api/users';
import { useAppStore } from '../store/appStore';
import { Icon } from './Icon';
import { UserRow } from './UserRow';

// Server and API errors already carry a German message; anything else (should not happen here)
// does not.
const messageOf = (e: unknown, fallback: string) =>
  e instanceof ApiError || e instanceof OfflineError ? e.message : fallback;

export function UsersScreen() {
  const goStart = useAppStore((s) => s.goStart);
  const sessionUser = useSession((s) => s.user);
  const logout = useSession((s) => s.logout);

  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  // A password to hand over right now, set once after a create or a reset. Never written
  // anywhere but here, so it is gone once this state clears or the screen is left.
  const [created, setCreated] = useState<{ username: string; password: string } | null>(null);

  const [username, setUsername] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [role, setRole] = useState<Role>('instructor');
  const [password, setPassword] = useState(() => generatePassword());
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    listUsers()
      .then(setUsers)
      .catch((e) => setError(messageOf(e, 'Benutzer konnten nicht geladen werden.')))
      .finally(() => setLoading(false));
  }, []);

  const submitCreate = async (e: FormEvent) => {
    e.preventDefault();
    setCreating(true);
    setError(null);
    try {
      const user = await createUser({ username, displayName, role, password });
      setUsers((prev) => [...prev, user]);
      setCreated({ username: user.username, password });
      setUsername('');
      setDisplayName('');
      setRole('instructor');
      setPassword(generatePassword());
    } catch (e) {
      setError(messageOf(e, 'Benutzer konnte nicht angelegt werden.'));
    } finally {
      setCreating(false);
    }
  };

  const patch = async (id: string, body: Parameters<typeof updateUser>[1]) => {
    setBusyId(id);
    setError(null);
    try {
      const updated = await updateUser(id, body);
      setUsers((prev) => prev.map((u) => (u.id === id ? updated : u)));
      return updated;
    } catch (e) {
      setError(messageOf(e, 'Änderung konnte nicht gespeichert werden.'));
      return null;
    } finally {
      setBusyId(null);
    }
  };

  const toggleDisabled = async (user: User) => {
    const verb = user.disabled ? 'entsperren' : 'sperren';
    if (!window.confirm(`Benutzer „${user.displayName}“ ${verb}?`)) return;
    await patch(user.id, { disabled: !user.disabled });
  };

  const remove = async (user: User) => {
    if (!window.confirm(`Benutzer „${user.displayName}“ endgültig löschen?`)) return;
    setBusyId(user.id);
    setError(null);
    try {
      await deleteUser(user.id);
      setUsers((prev) => prev.filter((u) => u.id !== user.id));
    } catch (e) {
      setError(messageOf(e, 'Benutzer konnte nicht gelöscht werden.'));
    } finally {
      setBusyId(null);
    }
  };

  // Someone else's password: shown once here on success. The admin's own is skipped below --
  // they already know what they just typed.
  const changePassword = async (user: User, newPassword: string) => {
    const updated = await patch(user.id, { password: newPassword });
    if (!updated) return false;
    setCreated({ username: user.username, password: newPassword });
    return true;
  };

  const changeOwnPassword = async (user: User, newPassword: string) => {
    const updated = await patch(user.id, { password: newPassword });
    if (!updated) return false;
    await logout();
    return true;
  };

  return (
    <div className="workspace">
      <header className="workspace-header simulation-header">
        <div className="workspace-back">
          <button
            type="button"
            onClick={goStart}
            className="button quiet"
            aria-label="Zur Übersicht"
          >
            <Icon name="back" />
            Übersicht
          </button>
        </div>
        <div className="workspace-title">
          <strong>Benutzerverwaltung</strong>
        </div>
      </header>
      <div className="flex-1 overflow-auto p-6 flex flex-col gap-8">
        {error ? (
          <p className="notice warning" role="alert">
            {error}
          </p>
        ) : null}
        {created ? (
          <div className="notice" role="alert">
            <p>
              Passwort für <strong>{created.username}</strong>: <code>{created.password}</code>
            </p>
            <p className="muted">
              Jetzt weitergeben. Es wird nirgends gespeichert und ist nach dem Schließen nicht mehr
              abrufbar.
            </p>
            <button type="button" className="button quiet" onClick={() => setCreated(null)}>
              Schließen
            </button>
          </div>
        ) : null}

        {loading ? (
          <output className="muted">Wird geladen …</output>
        ) : users.length === 0 ? (
          // A load failure leaves users empty too; the error banner above already covers it.
          error ? null : (
            <p className="muted">Keine Benutzer.</p>
          )
        ) : (
          <ul className="saved-run-list">
            {users.map((user) => (
              <UserRow
                key={user.id}
                user={user}
                isSelf={isSelfTarget(user.id, sessionUser?.id)}
                busy={busyId === user.id}
                onRoleChange={(next) => patch(user.id, { role: next })}
                onToggleDisabled={() => toggleDisabled(user)}
                onDelete={() => remove(user)}
                onPasswordChanged={(next) => changePassword(user, next)}
                onOwnPasswordChanged={(next) => changeOwnPassword(user, next)}
              />
            ))}
          </ul>
        )}

        <form
          className="flex max-w-sm flex-col gap-3 border-t border-neutral-800 pt-6"
          onSubmit={submitCreate}
        >
          <h2 className="text-sm font-medium">Neuen Benutzer anlegen</h2>
          <label className="flex flex-col gap-1 text-sm" htmlFor="new-username">
            <span>Benutzername</span>
            <input
              id="new-username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              pattern="[A-Za-z0-9._-]+"
              maxLength={64}
              required
            />
          </label>
          <label className="flex flex-col gap-1 text-sm" htmlFor="new-displayname">
            <span>Anzeigename</span>
            <input
              id="new-displayname"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              required
            />
          </label>
          <label className="flex flex-col gap-1 text-sm" htmlFor="new-role">
            <span>Rolle</span>
            <select id="new-role" value={role} onChange={(e) => setRole(e.target.value as Role)}>
              <option value="instructor">Ausbilder</option>
              <option value="admin">Administrator</option>
            </select>
          </label>
          <label className="flex flex-col gap-1 text-sm" htmlFor="new-password">
            <span>Passwort</span>
            <div className="flex gap-2">
              <input
                id="new-password"
                className="flex-1"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                minLength={12}
                maxLength={256}
                required
              />
              <button
                type="button"
                className="button quiet"
                onClick={() => setPassword(generatePassword())}
              >
                Neu erzeugen
              </button>
            </div>
          </label>
          <p className="muted">Wird nach dem Anlegen einmal angezeigt und nirgends gespeichert.</p>
          <button type="submit" className="button primary" disabled={creating}>
            <Icon name="plus" size={16} />
            Anlegen
          </button>
        </form>
      </div>
    </div>
  );
}
